import { View } from 'react-native';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { mergeStyles } from 'src/_shared/utils/mergeStyles';
import { styles, stylesV2 } from './ToolbarTile.styles';
import { BaseButton } from '@photosi/rn-components/buttons/base-button/BaseButton';
import { TBaseButtonProps } from '@photosi/rn-components/buttons/base-button/BaseButton.types';
import { Icon } from '@photosi/rn-components/icon/Icon';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { Tool } from '../BaseToolbar.types';
import { ToolOptionsProps } from '../toolbar-options/tool-options-renderer/ToolOptionsRenderer.types';
import Animated, { useAnimatedStyle, withTiming } from 'react-native-reanimated';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/contexts/StoreContext';
import { configurationTestID, generateTestID } from 'src/helpers/testIDs.helper';
import { stylesV3 } from './ToolbarTileV3.styles';
import { TestIDs } from 'src/constants/testIDs.constants';

export type ToolbarTileProps = {
  isExpanded: boolean;
  isSelected: boolean;
  iconName: ICON_NAMES;
  isV3?: boolean;
} & Tool<ToolOptionsProps> &
  Omit<TBaseButtonProps, 'children'>;

export const ToolbarTile = observer(
  ({
    tool,
    isSelected,
    isExpanded,
    iconName,
    style,
    testID,
    focusId,
    isV3,
    accessible = true,
    accessibilityLabel,
    accessibilityRole,
    ...buttonProps
  }: ToolbarTileProps) => {
    const { i18n } = useStore();
    const { titleKey, selectedChoice } = tool;
    const selectedToolTitleKey = selectedChoice?.titleKey;
    const toolDetailsAnimatedStyle = useAnimatedStyle(() => {
      return {
        opacity: withTiming(isExpanded ? 1 : 0, { duration: 500 }),
      };
    }, [isExpanded]);

    const styles = isV3 ? stylesV3 : stylesV2;

    const buttonTestID = testID ?? configurationTestID(tool.code);

    return (
      <BaseButton
        testID={buttonTestID}
        focusId={focusId ?? buttonTestID}
        style={mergeStyles([styles.button, style, isSelected && styles.buttonSelected])}
        focusStyle={styles.buttonFocus}
        hoverStyle={styles.buttonHover}
        disabledStyle={styles.buttonDisabled}
        innerStyle={styles.buttonInner}
        accessible={accessible}
        accessibilityLabel={
          accessibilityLabel ??
          (titleKey ? i18n.t(titleKey) : null) ??
          (selectedToolTitleKey ? i18n.t(selectedToolTitleKey) : null) ??
          `toolbar tile ${buttonTestID}`
        }
        accessibilityRole={accessibilityRole ?? 'button'}
        {...buttonProps}
      >
        {() => (
          <>
            <View style={styles.iconContainer} testID={generateTestID(buttonTestID, TestIDs.IMAGE_WRAPPER)}>
              <Icon
                name={iconName}
                width={styles.icon.width as number}
                height={styles.icon.height as number}
                color={(styles.icon as any).color}
              />
            </View>
            <View style={styles.toolDetailsContainer}>
              <Animated.View style={[styles.toolDetails, toolDetailsAnimatedStyle]}>
                {!!titleKey && (
                  <TextLato
                    style={mergeStyles(styles.toolDetailsText, styles.toolDetailsTitle)}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    testID={generateTestID(buttonTestID, TestIDs.LABEL)}
                  >
                    {i18n.t(titleKey)}
                  </TextLato>
                )}
                {!!selectedToolTitleKey ? (
                  <TextLato
                    style={mergeStyles(styles.toolDetailsText, styles.toolDetailsSubtitle)}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    testID={generateTestID(buttonTestID, TestIDs.TITLE)}
                  >
                    {i18n.isDictionaryKeyAvailable(`${selectedToolTitleKey}-active-label`)
                      ? i18n.t(`${selectedToolTitleKey}-active-label`)
                      : i18n.t(selectedToolTitleKey)}
                  </TextLato>
                ) : null}
              </Animated.View>
            </View>
          </>
        )}
      </BaseButton>
    );
  },
);

ToolbarTile.displayName = 'ToolbarTile';
